import React, { useEffect, useState, useMemo } from "react";
import "./SelectedContactTable.css";
import * as FeatherIcon from "react-feather";
import { checkContactExistsInCampaign, deleteCampaignContacts } from "../../../../../../services/iqApiV2";
import ContactDeleteCell from "./ContactDeleteCell";
import { useUser } from "../../../../../../context/UserContext";
const PAGE_SIZE = 5;

// Prefer _id/id/email/linkedin_url; if none, synthesize a stable one
const getBaseId = (c) => c?.db_id|| c?._id ||  c?.id || c?.email || c?.linkedin_url || null;

// Normalize: add __sid and de-duplicate by id
const normalizeUnique = (list = []) => {
  const seen = new Set();
  return list.reduce((acc, c, idx) => {
    const base = getBaseId(c);
    const sid = base ?? `__sid_${idx}`;
    if (seen.has(sid)) return acc;
    seen.add(sid);
    acc.push({ ...c, __sid: sid });
    return acc;
  }, []);
};

export default function SelectedContactTable({
  selectedContacts = [],
  setSelectedContacts = () => {},
  onClose = () => {},
  nonEditable = false,       // when true, hides the Delete action column
  user,  // kept for compatibility (unused here)
  account,
  disableForward,setDisableForward,
  campaignId
}) {
  const [localSelected, setLocalSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);


const [blockedIds, setBlockedIds] = useState(new Set());

useEffect(() => {
  async function fetchData() {
    const searchedIds = selectedContacts?.map((item) => item?.enrich_id || item?.id);
    const checkBusy = await checkContactExistsInCampaign(
      user?.company_name,
      account,
      searchedIds
    );

    const seen = new Set();
    const uniqueEnrich = checkBusy?.filter((item) => {
      const enrichId = item?.enrich_id || item?.id;
      if (seen.has(enrichId)) return false;
      seen.add(enrichId);
      return true;
    });

    console.log("uniqueEnrich", uniqueEnrich, campaignId);

    // ✅ Create a Set of enrich_ids whose last campaign _id is NOT the current campaignId
    const blocked = new Set(
      (uniqueEnrich || [])
        .filter((e) => {
          const lastCampaignId = e?.campaign?.[e?.campaign?.length - 1]?._id;
          return lastCampaignId && lastCampaignId !== campaignId;
        })
        .map((e) => e?.enrich_id)
        .filter(Boolean) // remove undefined/null enrich_ids
    );

    console.log("blocked", blocked);

    setBlockedIds(blocked); // ✅ Store for row-level access control

    if (blocked?.size > 0) {
      setDisableForward(true);
    }else{
      setDisableForward(false)
    }
    console.log("selectedContacts",blocked,selectedContacts)
  }

  fetchData();
  setLocalSelected(normalizeUnique(selectedContacts));
}, [selectedContacts]);


  // Reset to page 1 when query or row count changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, localSelected.length]);

  const getRowId = (c) => c?.__sid || getBaseId(c) || "";

  // Delete immediately from local + parent
  const handleDelete = async(id) => {
    console.log("ID TO DELETE",id)
    const resp = await deleteCampaignContacts(campaignId,id)
    if (resp?.success==200) {
      setLocalSelected((prev) => prev.filter((c) => getRowId(c) !== id));
      setSelectedContacts((prevParent) => {
      const normalized = normalizeUnique(prevParent);
      return normalized.filter((c) => (c.__sid || getBaseId(c)) !== id);
    });
    }

  };

  const getLocation = (c) =>
    [c?.city, c?.country].filter(Boolean).join(", ") || "—";

  const filteredContacts = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return localSelected.filter((c) => {
      const name = (c?.name || `${c?.first_name || ""} ${c?.last_name || ""}`).trim();
      const title = c?.title || c?.headline || "";
      const location = getLocation(c);
      return (
        (name && name.toLowerCase().includes(q)) ||
        (title && title.toLowerCase().includes(q)) ||
        (location && location.toLowerCase().includes(q))
      );
    });
  }, [localSelected, searchQuery]);

  // Pagination
  const total = filteredContacts.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, total);
  const pageRows = useMemo(
    () => filteredContacts.slice(startIdx, endIdx),
    [filteredContacts, startIdx, endIdx]
  );

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
  };

  // Keep currentPage valid after deletions
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // compact window of page numbers
  const getVisiblePages = (current, total, windowSize = 3) => {
    if (total <= windowSize) return Array.from({ length: total }, (_, i) => i + 1);
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, current - half);
    let end = start + windowSize - 1;
    if (end > total) {
      end = total;
      start = end - windowSize + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div className="sct-wrapper">
      <div className="sct-header-row">
        {/* No apply/update controls */}
      </div>

      <input
        className="sct-search"
        placeholder="Search by name, title or location..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <div className="sct-table-wrapper" role="region" aria-label="Selected contacts table">
        <table className="sct-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Title</th>
              <th>LinkedIn</th>
              {!nonEditable && <th></th>}
            </tr>
          </thead>

          <tbody>
            {pageRows.map((c) => {
              const id = getRowId(c);
              const name =
                c?.name || `${c?.first_name || ""} ${c?.last_name || ""}`.trim();
              return (
                <tr
                    key={id}
                    className={blockedIds.has(c?.enrich_id || c?.id) ? "sct-danger-row" : ""}
                  >
                  <td>{name || "—"}</td>
                  <td>
                    {c?.title ? c?.title: "—"}
                  </td>
                  <td>
                    {c?.linkedin_url ? (
                      <a
                        href={c.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View LinkedIn"
                      >
                        <i
                          className="fa fa-linkedin-square sct-linkedin-icon"
                          aria-hidden="true"
                        ></i>
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td >
                  <ContactDeleteCell
                      handleDelete={handleDelete}
                      id={id}
                      name={name}
                      nonEditable={!blockedIds.has(c.enrich_id) ? nonEditable : false}
                    />
                   </td>
                    
                
            
                </tr>
              );
            })}
          </tbody>
        </table>

        {total === 0 && <div className="sct-empty-msg">No contacts found.</div>}
      </div>

      {total > 0 && (
        <div className="sct-pagination">
          <div className="scrc-pageinfo ml-2">
            Showing {startIdx + 1} to {endIdx} of {total}
          </div>

          <div className="sct-page-controls">
            <button
              className="sct-nav-link"
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              aria-label="First page"
            >
              First
            </button>

            <button
              className="sct-chevron"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              ‹
            </button>

            {getVisiblePages(currentPage, totalPages).map((p) => (
              <button
                key={p}
                className={`scrc-pagebtn ${p === currentPage ? "is-active" : ""}`}
                onClick={() => goToPage(p)}
                aria-current={p === currentPage ? "page" : undefined}
              >
                {p}
              </button>
            ))}

            <button
              className="sct-chevron"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              ›
            </button>

            <button
              className="sct-nav-link"
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              aria-label="Last page"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
